import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { animeList, animeCategories } from '@/mocks/anime';

const categoryMap: Record<string, string> = {
  riman: '日漫',
  guoman: '国漫',
  jingdian: '经典番剧',
  rexue: '热血',
  lianai: '恋爱',
  kehuan: '科幻',
  xuanhuan: '玄幻',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const animeId = Number(id);

  const anime = useMemo(() => animeList.find((a) => a.id === animeId), [animeId]);

  const relatedAnime = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((a) => a.id !== anime.id && a.category === anime.category)
      .slice(0, 4);
  }, [anime]);

  if (!anime) {
    return (
      <>
        <ScrollToTop />
        <header><Navbar /></header>
        <main className="pt-24 pb-16">
          <div className="container-custom text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-800 rounded-full">
              <i className="ri-error-warning-line text-4xl text-foreground-600"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-50 mb-2">未找到该动漫</h1>
            <p className="text-foreground-500 mb-6">该番剧可能已下架或不存在</p>
            <Link to="/" className="btn-primary rounded-full px-8 py-3">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryMap[anime.category] || anime.category;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${anime.title} - 6080影视免费高清在线观看`,
    description: anime.description,
    datePublished: `${anime.year}-01-01`,
    dateModified: anime.updateTime,
    author: { '@type': 'Organization', name: '6080影视' },
    publisher: { '@type': 'Organization', name: '6080影视' },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://6080ys.com/' },
        { '@type': 'ListItem', position: 2, name: categoryName, item: `https://6080ys.com/category/${anime.category}` },
        { '@type': 'ListItem', position: 3, name: anime.title },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>

      <main>
        <div className="pt-20 pb-0">
          <div className="container-custom">
            <nav className="flex items-center gap-2 text-xs md:text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <Link to={`/category/${anime.category}`} className="text-foreground-500 hover:text-primary-400 transition-colors">{categoryName}</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300 line-clamp-1">{anime.title}</span>
            </nav>
          </div>
        </div>

        <section className="pb-8">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="md:w-64 flex-shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-800 sticky top-24">
                  <img
                    src={anime.coverImage}
                    alt={anime.title}
                    title={`${anime.title} - 6080影视免费高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  {anime.title}
                </h1>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500/20 text-accent-400 text-sm font-medium">
                    <i className="ri-star-fill text-xs"></i>
                    {anime.rating}
                  </div>
                  <span className="text-foreground-600 text-sm">{anime.region}</span>
                  <span className="text-foreground-700">·</span>
                  <span className="text-foreground-600 text-sm">{anime.year}</span>
                  <span className="text-foreground-700">·</span>
                  <span className={`text-sm font-medium ${anime.status === '更新中' ? 'text-primary-400' : 'text-secondary-400'}`}>
                    {anime.status}
                  </span>
                  <span className="text-foreground-700">·</span>
                  <span className="text-foreground-600 text-sm">
                    <i className="ri-eye-line mr-1"></i>
                    {anime.views > 10000 ? `${(anime.views / 10000).toFixed(0)}万次播放` : `${anime.views}次播放`}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full bg-background-800 text-foreground-400 text-xs border border-background-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-800/50 rounded-xl p-4 md:p-5 border border-background-700/50 mb-6">
                  <h3 className="text-foreground-200 font-medium text-sm mb-2">剧情简介</h3>
                  <p className="text-foreground-400 text-sm leading-relaxed">{anime.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-foreground-200 font-medium text-sm">
                      剧集列表 · 共 {anime.episodes.length} 集
                    </h3>
                    <span className="text-foreground-600 text-xs">
                      更新至第 {anime.episodes.length} 集 · {anime.updateTime}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                    {anime.episodes.map((ep) => (
                      <button
                        key={ep.id}
                        className="flex items-center justify-center px-3 py-2.5 rounded-lg text-sm bg-background-800 border border-background-700 text-foreground-300 hover:border-primary-500/40 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        第{ep.episode}集
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="btn-primary rounded-full px-8 py-3 text-sm cursor-pointer">
                    <i className="ri-play-circle-line mr-2 text-lg"></i>
                    立即播放 第1集
                  </button>
                  <button className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-800 border border-background-700 text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    <span className="text-sm">收藏</span>
                  </button>
                  <button className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-800 border border-background-700 text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line"></i>
                    <span className="text-sm">分享</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedAnime.length > 0 && (
          <section className="py-10 md:py-14 bg-background-900/50">
            <div className="container-custom">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-50">相关推荐</h2>
                  <p className="text-foreground-500 text-sm mt-1">喜欢这部的人也在看</p>
                </div>
                <Link to={`/category/${anime.category}`} className="text-primary-400 text-sm hover:text-primary-300 transition-colors whitespace-nowrap">
                  更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedAnime.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="anime-card group"
                  >
                    <div className="aspect-[2/3] overflow-hidden rounded-lg bg-background-800">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                        title={`${item.title} - 6080影视免费高清在线观看`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500/90 text-background-950 text-xs font-bold">
                        {item.rating}
                      </div>
                    </div>
                    <div className="mt-2.5">
                      <h4 className="text-foreground-100 text-sm font-medium line-clamp-1 group-hover:text-primary-400 transition-colors">
                        {item.title}
                      </h4>
                      <p className="text-foreground-600 text-xs mt-1">{item.region} · {item.year}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    </>
  );
}